import React, { useMemo, memo, useRef, useEffect, useState } from 'react';
import markdownit from 'markdown-it';
import { Typography } from 'antd';
import './custom.css';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';

// 创建markdown-it实例，但不处理图片
const createMarkdownIt = () => {
  const md = markdownit({
    html: false,
    breaks: true,
    linkify: true,
    typographer: true,
    highlight: (str: string, lang: string): string => {
      if (lang && hljs.getLanguage(lang)) {
        try {
          return `<pre class="hljs"><code>${
            hljs.highlight(str, { 
              language: lang, 
              ignoreIllegals: true 
            }).value
          }</code></pre>`;
        } catch (error) {
          console.warn('Highlight.js error:', error);
        }
      }
      return `<pre class="hljs"><code>${markdownit().utils.escapeHtml(str)}</code></pre>`;
    }
  });

  return md;
};

const md = createMarkdownIt();

// 图片预览组件
const ImagePreview: React.FC<{
  src: string;
  alt: string;
  isOpen: boolean;
  onClose: () => void;
}> = memo(({ src, alt, isOpen, onClose }) => {
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeydown);
      document.body.style.overflow = 'hidden'; // 防止背景滚动
    }

    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="image-preview-overlay" onClick={onClose}>
      <div className="image-preview-container" onClick={(e) => e.stopPropagation()}>
        <img 
          src={src} 
          alt={alt}
          className="image-preview-img"
        />
        {alt && (
          <div className="image-preview-caption">{alt}</div>
        )}
        <button 
          className="image-preview-close"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
      </div>
    </div>
  );
});

// 图片组件
const MarkdownImage: React.FC<{ 
  src: string; 
  alt: string; 
  title: string;
  onImageClick: (src: string, alt: string) => void;
}> = memo(({ src, alt, title, onImageClick }) => {
  const handleClick = () => {
    onImageClick(src, alt);
  };

  return (
    <div className="markdown-image-container">
      <img 
        src={src} 
        alt={alt} 
        title={title}
        className="markdown-image"
        loading="lazy"
        onClick={handleClick}
      />
      {alt && <div className="markdown-image-caption">{alt}</div>}
    </div>
  );
});

// 解析markdown中的图片
const parseImages = (content: string): { 
  type: 'text' | 'image'; 
  content: string; 
  src?: string; 
  alt?: string; 
  title?: string;
}[] => {
  const result: { type: 'text' | 'image'; content: string; src?: string; alt?: string; title?: string }[] = [];
  let remaining = content;
  
  // 图片正则表达式 - 改进版本，支持更多格式
  const imageRegex = /!\[(.*?)\]\((.*?)(?:\s+"(.*?)")?\)/g;
  let lastIndex = 0;
  let match;

  while ((match = imageRegex.exec(remaining)) !== null) {
    // 添加前面的文本
    if (match.index > lastIndex) {
      result.push({
        type: 'text',
        content: remaining.slice(lastIndex, match.index)
      });
    }

    // 添加图片
    const alt = match[1] || '';
    const src = match[2] || '';
    const title = match[3] || '';
    
    result.push({
      type: 'image',
      content: match[0],
      src,
      alt,
      title
    });

    lastIndex = match.index + match[0].length;
  }

  // 添加剩余的文本
  if (lastIndex < remaining.length) {
    result.push({
      type: 'text',
      content: remaining.slice(lastIndex)
    });
  }

  return result;
};

export const StableMarkdownRenderer: React.FC<{ content: string }> = memo(({ content }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [previewImage, setPreviewImage] = useState<{ src: string; alt: string } | null>(null);

  // 处理图片点击
  const handleImageClick = (src: string, alt: string) => {
    setPreviewImage({ src, alt });
  };

  // 关闭预览
  const handleClosePreview = () => {
    setPreviewImage(null);
  };

  // 解析内容并渲染
  const renderedContent = useMemo(() => {
    if (!content) return null;

    const needsMarkdown = /^#|```|`[^`]+`|\*\*[^*]+\*\*|\[.*\]\(.*\)/.test(content.trim());
    
    if (!needsMarkdown) {
      return (
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
          {content}
        </div>
      );
    }

    // 检查是否包含图片
    if (content.includes('![')) {
      const parts = parseImages(content);
      
      return (
        <div ref={containerRef} className="markdown-content">
          {parts.map((part, index) => {
            if (part.type === 'image') {
              return (
                <MarkdownImage
                  key={`image-${index}-${part.src}`}
                  src={part.src || ''}
                  alt={part.alt || ''}
                  title={part.title || ''}
                  onImageClick={handleImageClick}
                />
              );
            } else {
              // 对文本部分使用markdown渲染
              return (
                <span
                  key={`text-${index}`}
                  dangerouslySetInnerHTML={{ __html: md.render(part.content) }}
                />
              );
            }
          })}
        </div>
      );
    } else {
      // 没有图片的内容直接渲染
      return (
        <div
          ref={containerRef}
          className="markdown-content"
          dangerouslySetInnerHTML={{ __html: md.render(content) }}
        />
      );
    }
  }, [content]);

  return (
    <>
      {renderedContent}
      {previewImage && (
        <ImagePreview
          src={previewImage.src}
          alt={previewImage.alt}
          isOpen={true}
          onClose={handleClosePreview}
        />
      )}
    </>
  );
});